import { Grid, Typography, makeStyles } from '@material-ui/core';
import { Slider, colors, moneyFormat } from 'design-system';
import React from 'react';

import {
  limitForMaxValue,
  limitsHaveRange,
  regularCoverages,
} from 'data-layer-cr/dist/functions/coverages';
import {
  coverageForCode,
  getRegularCoverages,
} from 'data-layer-cr/dist/functions/quotations';
import {
  CoverageCatalog,
  CoverageCatalogResume,
  Quotation,
  QuotationCoverage,
} from 'data-layer-cr/dist/models';
import { CoverageLimit } from 'data-layer-cr/dist/models/CoverageCatalog';
import CatalogItem from '../../../../../models/helperTypes/CatalogItem';
import PricingProfileResponse from '../../../../../models/responseTypes/PricingProfileResponse';
import { DefaultCard, DefaultCardRow } from '../../../../General';

const { formatMoney, formatShortMoney } = moneyFormat;

type Props = {
  quotation: Quotation;
  coverages: CoverageCatalog[];
  coverageResumes: CoverageCatalogResume[];
  onUpdateSelectedValue: (
    coverageCode: string,
    newValue: CoverageLimit,
  ) => void;
  selectedValues: QuotationCoverage[];
  currentProfile: PricingProfileResponse;
};

const useStyles = makeStyles({
  informationSegment: {
    paddingTop: 6,
    paddingBottom: 10,
    textAlign: 'center',
  },
  imageSpacing: {
    marginRight: 14.43,
  },
  coverageName: {
    fontSize: 15,
    fontWeight: 400,
    color: colors.abbey,
    letterSpacing: 0.1,
  },
  coverageNameSliderWithoutRanges: {
    marginLeft: 4,
  },
  coverageDescription: {
    marginRight: 50,
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: 0.1,
  },
});

const DynamicCoverageCard = ({
  quotation,
  coverages,
  coverageResumes,
  onUpdateSelectedValue,
  selectedValues,
  currentProfile,
}: Props): JSX.Element => {
  const classes = useStyles();
  const displayValues: React.ReactNode[] = [];
  const additionalCoverages = regularCoverages(coverages);

  /**
   * Returns a slider configured for the provide coverage
   *
   * @param coverage - The coverage that contains the slider limits
   * @param selectedLimit - The selected value
   * @param updateMethod - The update method
   */
  function makeSlider(
    coverage: CoverageCatalog,
    selectedMaxValue: number,
    updateMethod: (code: string, newValue: CoverageLimit) => void,
    index: number,
  ): React.ReactNode {
    const sliderItems: CatalogItem[] = [];
    coverage.limits.forEach((limit): void => {
      const textValue = limit.maximum.toString();
      const displayText = `${formatShortMoney(
        limit.minimum,
        currentProfile.typeOfMoney,
      )} / ${formatShortMoney(limit.maximum, currentProfile.typeOfMoney)}`;
      sliderItems.push({
        Value: textValue,
        Text: displayText,
        Object: limit,
      });
    });

    return (
      <Slider
        inputId={`sales-web-button-coverageslider-${index}`}
        inputName={`coverage-slider-${coverage.code}`}
        inputLabel=""
        defaultValue={selectedMaxValue.toString()}
        onChange={(val: CatalogItem): void => {
          updateMethod(coverage.code, val.Object as CoverageLimit);
        }}
        options={sliderItems}
      />
    );
  }

  /**
   * Returns a row representing a coverage with a single limit
   *
   * @param coverage - The coverage to render
   */
  function makeRowForSingleLimit(
    coverage: CoverageCatalog,
    index: number,
  ): React.ReactNode {
    const usedLimit = coverage.limits[0];
    let coverageText = `${formatShortMoney(
      usedLimit.minimum,
      currentProfile.typeOfMoney,
    )} / ${formatShortMoney(usedLimit.maximum, currentProfile.typeOfMoney)}`;
    if (!limitsHaveRange(coverage)) {
      coverageText = formatMoney(
        usedLimit.maximum,
        false,
        currentProfile.typeOfMoney,
      );
    }
    return (
      <DefaultCardRow
        key={coverage.code}
        displayItems={{
          left: <Typography variant="body2">{coverage.name}</Typography>,
          right: (
            <Typography
              id={`sales-web-label-moneycoverage-${index}`}
              variant="h2"
              style={{ fontSize: 23 }}
            >
              {coverageText}
            </Typography>
          ),
        }}
      />
    );
  }

  /**
   * Returns a row representing a coverage with the value comming from the quotation
   *
   * @param coverage - The coverage to render
   */
  function makeRowForQuotationSingleValue(
    coverage: CoverageCatalog,
    index: number,
  ): React.ReactNode {
    const curValue =
      getRegularCoverages(quotation, coverageResumes).filter((cov): boolean => {
        return cov.coverageId === coverage.code;
      })[0]?.insuredValue || 0;
    return (
      <DefaultCardRow
        key={coverage.code}
        displayItems={{
          left: (
            <Typography variant="body2" className={classes.coverageName}>
              {coverage.name}
            </Typography>
          ),
          right: (
            <Typography
              id={`sales-web-label-moneycoverage-${index}`}
              variant="h2"
              style={{ fontSize: 23 }}
            >
              {formatMoney(curValue, false, currentProfile.typeOfMoney)}
            </Typography>
          ),
        }}
      />
    );
  }

  /**
   * Returns a row representing a coverage modifiable by a slider.
   * The slider wont't show limits.
   *
   * @param coverage - The coverage to render
   * @param value - The current selected value in the slider
   */
  function makeSliderRowWithoutRanges(
    coverage: CoverageCatalog,
    value: number,
    index: number,
  ): React.ReactNode {
    let image = { src: '', alt: '' };
    if (coverage.name === 'Lesiones Corporales') {
      image = {
        src: '/svg/bodily_injury.svg',
        alt: 'Bodily Injury',
      };
    }
    if (coverage.name === 'Daños a Terceros') {
      image = {
        src: '/svg/carDemage.svg',
        alt: 'Car Demage',
      };
    }
    if (coverage.name === 'Asistencia Médica') {
      image = {
        src: '/svg/medical_assistance.svg',
        alt: 'Medical Assistance',
      };
    }
    return (
      <DefaultCardRow key={coverage.code}>
        <Grid container>
          {image.src !== '' ? (
            <img
              src={image.src}
              alt={image.alt}
              className={classes.imageSpacing}
            />
          ) : null}
          <Typography className={classes.coverageName}>
            {coverage.name}
          </Typography>
        </Grid>
        <Grid className={classes.informationSegment} container>
          <Typography
            id={`sales-web-label-moneycoverage-${index}`}
            variant="h2"
            style={{ fontSize: 23 }}
          >
            {formatMoney(value, false, currentProfile.typeOfMoney)}
          </Typography>
        </Grid>
        {currentProfile.type === 'colectiva'
          ? null
          : makeSlider(coverage, value, onUpdateSelectedValue, index)}
      </DefaultCardRow>
    );
  }

  /**
   * Returns a row representing a coverage modifiable by a slider.
   * the slider will show limits with ranges.
   *
   * @param coverage - The coverage to render
   * @param value - The current selected value in the slider
   */
  function makeSliderWithRanges(
    coverage: CoverageCatalog,
    value: number,
    index: number,
  ): React.ReactNode {
    const usedLimit = limitForMaxValue(coverage, value);
    return (
      <DefaultCardRow key={coverage.code}>
        <Grid container>
          <img
            src="/svg/bodily_injury.svg"
            alt="Bodily Injury"
            className="logo"
          />
          <Typography className={classes.coverageName}>
            {coverage.name}
          </Typography>
        </Grid>
        <Grid
          className={classes.informationSegment}
          container
          alignItems="center"
        >
          <Typography
            id={`sales-web-label-moneycoverage-${index}`}
            variant="h2"
            style={{ fontSize: 23 }}
          >
            {typeof usedLimit !== 'undefined'
              ? `${formatMoney(
                  usedLimit.minimum,
                  false,
                  currentProfile.typeOfMoney,
                )} / ${formatMoney(
                  usedLimit.maximum,
                  false,
                  currentProfile.typeOfMoney,
                )}`
              : formatMoney(0, false, currentProfile.typeOfMoney)}
          </Typography>
          <Grid container direction="row">
            <Typography className={classes.coverageDescription}>
              personas
            </Typography>
            <Typography className={classes.coverageDescription}>
              accidentes
            </Typography>
          </Grid>
        </Grid>
        {currentProfile.type === 'colectiva'
          ? null
          : makeSlider(coverage, value, onUpdateSelectedValue, index)}
      </DefaultCardRow>
    );
  }

  // Render each row
  additionalCoverages.forEach(
    (coverage: CoverageCatalog, index: number): void => {
      let valueForCoverage = 0;
      const usedSimplifiedCoverage = coverageForCode(
        selectedValues,
        coverage.code,
      );
      if (typeof usedSimplifiedCoverage !== 'undefined') {
        valueForCoverage = usedSimplifiedCoverage.insuredValue;
      }

      if (coverage.limits.length === 1) {
        displayValues.push(makeRowForSingleLimit(coverage, index));
      } else if (coverage.limits.length <= 0) {
        displayValues.push(
          makeSliderRowWithoutRanges(coverage, valueForCoverage, index),
        );
      } else if (!limitsHaveRange(coverage)) {
        displayValues.push(makeRowForQuotationSingleValue(coverage, index));
      } else {
        displayValues.push(
          makeSliderWithRanges(coverage, valueForCoverage, index),
        );
      }
    },
  );

  return (
    <DefaultCard
      title="Coberturas"
      imageTitle={
        <img
          src="/svg/coverage_shield.svg"
          alt="coberturas"
          style={{ marginRight: 7, marginBottom: 15 }}
        />
      }
    >
      {displayValues}
    </DefaultCard>
  );
};

export default DynamicCoverageCard;
